import React, { useState, useEffect, useMemo } from 'react';
import {
  fetchHigherViewStatic,
  fetchHigherViewFYAll,
  downloadDashboardEmployee,
  downloadDashboardSubbie,
  downloadCurrentMonth,
  downloadHigherviewFY,
} from '../utils/api';
import { cssUrl } from '../utils/cssVersion';
import HelpButton from '../components/shared/HelpButton';
import { ErrorIcon } from '../components/shared/Icons';

/* Reuse the FilterTemplate stylesheet (same one SubbiePayments loads). */
const filterTemplateStyles = cssUrl('filterTemplate.css');

/* -------------------------------------------------------------------------
 * HigherView — four tabs (mirrors dashboard.html nav-pills + the helpers):
 *   Employees -> get_table_dashemployee()  (static, one row)
 *   Subbies   -> get_table_dashsubbie()    (static, one row)
 *   Current   -> get_table_higherview()    (static, per-subbie rows)
 *   Higher    -> get_higherview_fy()        (dynamic: FY + chips + nav)
 *
 * Styling: FilterTemplate (fr-*) classes only, with inline styles where FT has
 * no equivalent (metric grid, totals pills, nav buttons, section heads).
 * ---------------------------------------------------------------------- */

const TABS = [
  { key: 'employee', label: 'Employees' },
  { key: 'subbie', label: 'Subbies' },
  { key: 'current', label: 'Current' },
  { key: 'higher', label: 'Higher View' },
];

/* Per-tab header metadata: the title beside the help icon, the help PDF, and
 * the Excel download(s). `needsFy` downloads receive the current FY start. */
const TAB_META = {
  employee: {
    title: 'Employees',
    help: { chip: 'higherview_employee', urlFilter: '/static/pdffilters/ExpensesOverview.pdf' },
    downloads: [{ title: 'Download Employee dashboard (Excel)', filename: 'dashboard_employee.xlsx', fetch: downloadDashboardEmployee }],
  },
  subbie: {
    title: 'Subbies',
    help: { chip: 'higherview_subbie', urlFilter: '/static/pdffilters/ExpensesOverview.pdf' },
    downloads: [{ title: 'Download Subbie dashboard (Excel)', filename: 'dashboard_subbie.xlsx', fetch: downloadDashboardSubbie }],
  },
  current: {
    title: 'Current',
    help: { chip: 'higherview_current', urlFilter: '/static/pdffilters/ExpensesOverview.pdf' },
    downloads: [{ title: 'Download Current Month (Excel)', filename: 'current_month.xlsx', fetch: downloadCurrentMonth }],
  },
  higher: {
    title: 'Higher View',
    help: { chip: 'higherview', urlFilter: '/static/pdffilters/ExpensesOverview.pdf' },
    downloads: [{ title: 'Download Financial Year (Excel)', needsFy: true, fetch: downloadHigherviewFY }],
  },
};

const EXPENSE_CHIPS = [
  { label: 'Employee', value: 'Employee' },
  { label: 'SNL', value: 'SNL' },
  { label: 'Partnership', value: 'Partnership' },
  { label: 'Kanu', value: 'Kanu' },
  { label: 'Lucky', value: 'Lucky' },
  { label: 'Green Commercial', value: 'Green' },
  { label: 'Thunder Storm Cleaning', value: 'Thunder' },
  { label: 'Kasun', value: 'Kasun' },
  { label: 'Grand Total', value: 'Total' },
];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const fyStartForDate = (d) => {
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  return m >= 7 ? y : y - 1;
};
const yearForMonthInFY = (monthNum, fyStart) => (monthNum >= 7 ? fyStart : fyStart + 1);
const stepMonth = (monthNum, delta) => (((monthNum - 1 + delta) % 12 + 12) % 12) + 1;

const parseCurrency = (s) => {
  if (typeof s === 'number') return s;
  const n = parseFloat(String(s ?? '').replace(/[$,]/g, ''));
  return Number.isFinite(n) ? n : 0;
};
const fmt = (n) =>
  `$${Number(n).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/* ---------- inline style tokens (FT has no class for these) ---------- */
const S = {
  panel: {
    position: 'relative', overflow: 'hidden',
    background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
    border: '1px solid rgba(34, 211, 238, 0.18)', borderRadius: 16, padding: 16,
  },
  panelAccent: {
    position: 'absolute', top: 0, left: 0, right: 0, height: 3,
    background: 'linear-gradient(90deg, #22d3ee, #3b82f6, #8b5cf6)',
  },
  title: { margin: 0, fontSize: '1.05rem', fontWeight: 700, color: '#f1f5f9' },
  cards: { display: 'grid', gridTemplateColumns: '1fr', gap: 12, width: '100%' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' },
  cell: { minWidth: 0, padding: '2px 14px' },
  cellLabel: {
    display: 'block', fontSize: '0.62rem', letterSpacing: '0.04em',
    textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', marginBottom: 4,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  cardTitle: { display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9rem', fontWeight: 700, color: '#f1f5f9' },
  dot: { width: 8, height: 8, borderRadius: '50%', background: '#22d3ee', boxShadow: '0 0 8px rgba(34,211,238,0.6)', flexShrink: 0 },
  sectionHead: { display: 'flex', alignItems: 'baseline', gap: 10, margin: '0 0 12px' },
  eyebrow: { fontSize: '0.62rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: '#22d3ee', fontWeight: 700 },
  sectionTitle: { fontSize: '1rem', fontWeight: 600, color: '#e2e8f0', margin: 0 },
  divider: { display: 'flex', alignItems: 'center', gap: 12, margin: '8px 2px 2px', color: 'rgba(255,255,255,0.4)', fontSize: '0.68rem', letterSpacing: '0.12em', textTransform: 'uppercase' },
  rule: { flex: 1, height: 1, background: 'rgba(255,255,255,0.08)' },
  totalsWrap: { display: 'flex', gap: 8, padding: '4px 0 12px', flexWrap: 'wrap' },
  totalPill: { flex: '1 1 0', minWidth: 120, textAlign: 'center', borderRadius: 12, padding: '8px 10px', background: 'rgba(34,211,238,0.08)', border: '1px solid rgba(34,211,238,0.25)' },
  totalLabel: { fontSize: '0.68rem', color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.05em' },
  totalValue: { fontSize: '0.95rem', fontWeight: 700, color: '#22d3ee', whiteSpace: 'nowrap' },
  nav: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, marginBottom: 14 },
  navGroup: { display: 'flex', alignItems: 'center', gap: 6 },
  navLabel: { fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'rgba(255,255,255,0.5)', minWidth: 46 },
  navBtn: { minWidth: 38, padding: '8px 12px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.14)', background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer' },
  navCurrent: { minWidth: 90, padding: '8px 12px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #22d3ee, #3b82f6)', color: '#fff', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer' },
  selection: { marginLeft: 10, fontSize: '0.95rem', fontWeight: 500, color: '#7dd3fc' },
  link: { background: 'none', border: 'none', color: '#22d3ee', cursor: 'pointer', fontWeight: 600, padding: 0 },
  empty: { padding: '28px 16px', textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontStyle: 'italic' },
  blockCols: '1.2fr repeat(4, minmax(0, 1fr))',
  blockColHead: { fontSize: '0.62rem', letterSpacing: '0.05em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  blockRowLabel: { fontSize: '0.85rem', fontWeight: 700, color: '#22d3ee', whiteSpace: 'nowrap' },
  blockValue: { textAlign: 'right', fontSize: '0.86rem' },
};

/* =========================================================================
 * Shared pieces
 * ===================================================================== */

const SectionHead = ({ eyebrow, title, badge }) => (
  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, margin: '0 0 12px' }}>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, minWidth: 0 }}>
      {eyebrow && <span style={S.eyebrow}>{eyebrow}</span>}
      {title && <h5 style={S.sectionTitle}>{title}</h5>}
    </div>
    {badge != null && badge !== '' && (
      <span className="fr-card-date-end" style={{ fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap' }}>{badge}</span>
    )}
  </div>
);

const FilterBar = ({ label }) => (
  <div className="fr-results-bar" style={{ margin: '0 0 16px' }}>
    <div className="fr-results-info">
      <span className="fr-results-title">Filtering</span>
      <span className="fr-results-count">{label}</span>
    </div>
  </div>
);

const Empty = ({ text }) => (
  <div className="fr-empty-state">
    <div className="fr-empty-icon">📊</div>
    <p className="fr-empty-title">{text}</p>
  </div>
);

const Loading = ({ text }) => (
  <div className="fr-loading-state">
    <div className="fr-spinner" />
    <p className="fr-loading-text">{text}</p>
  </div>
);

const Panel = ({ children }) => (
  <div style={S.panel}><div style={S.panelAccent} />{children}</div>
);

/* Excel/spreadsheet glyph for the download buttons */
const SheetIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 3v4a1 1 0 0 0 1 1h4" />
    <path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" />
    <path d="M8 9h2M8 13h8M8 17h8" />
  </svg>
);

/* Same pattern as SubbiePayments' DownloadAction — fetch a blob, trigger a
 * browser download, surface failures via the toast. `arg` is passed to fetch
 * (the FY start for Higher View, undefined for the static tabs). */
const DownloadAction = ({ arg, setToast, fetchFile, filename, title, icon }) => {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    try {
      setBusy(true);
      const blob = await fetchFile(arg);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setToast({ message: e.message || 'Download failed', type: 'error' });
    } finally {
      setBusy(false);
    }
  };
  return (
    <button type="button" className="fr-download-btn" onClick={onClick} disabled={busy} title={title} style={busy ? { opacity: 0.5 } : undefined}>
      {icon}
    </button>
  );
};

const Toast = ({ toast }) => {
  if (!toast) return null;
  const err = toast.type === 'error';
  return (
    <div style={{
      position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 1000,
      padding: '10px 16px', borderRadius: 10, fontSize: '0.85rem', fontWeight: 600,
      background: err ? 'rgba(239,68,68,0.15)' : 'rgba(34,197,94,0.15)',
      border: `1px solid ${err ? 'rgba(239,68,68,0.4)' : 'rgba(34,197,94,0.4)'}`,
      color: err ? '#f87171' : '#4ade80',
    }}>
      {toast.message}
    </div>
  );
};

/* =========================================================================
 * Employee / Subbie static panels
 * ===================================================================== */

const PRIOR_HEADERS = ['Prior Month', 'Current Month', 'Actual YTD Annual', 'Budget YTD Annual'];
const BUDGET_HEADERS = ['Budget Current', 'Actual Current', 'Budget Annual', 'Actual Annual'];

const ROW_VARIANT = {
  total: { bg: 'rgba(34,211,238,0.10)', label: '#22d3ee' },
  grand: { bg: 'rgba(139,92,246,0.16)', label: '#c4b5fd' },
};

/* One blocked table: column headers across the top once, the label column
 * (salary type / subbie name) down the side, data rows beneath, and optional
 * Total / Grand Total bands. Optional date badge sits top-right of the header. */
const TableBlock = ({ eyebrow, title, badge, labelHead, headers, rows }) => (
  <div style={{ marginBottom: 26 }}>
    <SectionHead eyebrow={eyebrow} title={title} badge={badge} />
    <div className="fr-card" style={{ cursor: 'default' }}>
      <div style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: 540 }}>
          {/* sub header — one instance across the top */}
          <div style={{ display: 'grid', gridTemplateColumns: S.blockCols, alignItems: 'end', padding: '0 8px 10px', marginBottom: 2, borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
            <span style={S.blockColHead}>{labelHead}</span>
            {headers.map((h) => (<span key={h} style={{ ...S.blockColHead, textAlign: 'right' }}>{h}</span>))}
          </div>
          {/* data rows + optional total/grand bands; labels are the main header */}
          {rows.map((r, i) => {
            const v = r.variant ? ROW_VARIANT[r.variant] : null;
            return (
              <div
                key={`${r.label}-${i}`}
                style={{
                  display: 'grid', gridTemplateColumns: S.blockCols, alignItems: 'center',
                  padding: '9px 8px',
                  marginTop: v ? 6 : 0,
                  background: v ? v.bg : 'transparent',
                  borderRadius: v ? 8 : 0,
                  borderTop: (!v && i) ? '1px solid rgba(255,255,255,0.06)' : 'none',
                }}
              >
                <span style={{ ...S.blockRowLabel, color: v ? v.label : S.blockRowLabel.color }}>{r.label}</span>
                {r.values.map((val, j) => (
                  <span key={j} className="fr-card-value" style={S.blockValue}>{val === '' || val == null ? '—' : val}</span>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  </div>
);

const StaticPanel = ({ subject, filterLabel, rowLabels, row }) => {
  if (!row) return <Panel><Empty text="No data available." /></Panel>;

  const priorRows = [
    { label: rowLabels[0], values: [row[1], row[2], row[3], row[4]] },
    { label: rowLabels[1], values: [row[5], row[6], row[7], row[8]] },
    { label: rowLabels[2], values: [row[9], row[10], row[11], row[12]] },
    { label: rowLabels[3], values: [row[13], row[14], row[15], row[16]] },
  ];
  const budgetRows = [
    { label: rowLabels[0], values: [row[17], row[18], row[19], row[20]] },
    { label: rowLabels[1], values: [row[21], row[22], row[23], row[24]] },
    { label: rowLabels[2], values: [row[25], row[26], row[27], row[28]] },
    { label: rowLabels[3], values: [row[29], row[30], row[31], row[32]] },
  ];

  return (
    <Panel>
      <FilterBar label={filterLabel} />
      <TableBlock eyebrow="Snapshot" title={`Dashboard Prior Vs Current ${subject}`} labelHead="Salary Type" headers={PRIOR_HEADERS} rows={priorRows} />
      <TableBlock eyebrow="This month" title={`Dashboard Current Month Budget Vs Actuals ${subject}`} labelHead="Salary Type" headers={BUDGET_HEADERS} rows={budgetRows} />
    </Panel>
  );
};

/* =========================================================================
 * Current panel
 * ===================================================================== */

const MONEY_COLS = ['PO Inc GST / Gross', 'PO Ex GST / Net', 'GST / PAYG', 'Super'];
const anyNonZero = (vals) => vals.some((v) => parseCurrency(v) !== 0);

const CurrentPanel = ({ rows }) => {
  if (!rows || rows.length === 0) return <Panel><Empty text="No current-month data." /></Panel>;

  const meomDate = rows[0][2];
  const eomDate = rows[0][7];
  const meomRows = rows.filter((r) => anyNonZero([r[3], r[4], r[5], r[6]]));

  const midData = [
    ...meomRows.map((r) => ({ label: r[1], values: [r[3], r[4], r[5], r[6]] })),
    { label: 'Total', variant: 'total', values: [rows[0][12], rows[0][13], rows[0][14], rows[0][15]] },
  ];
  const eomData = [
    ...rows.map((r) => ({ label: r[1], values: [r[8], r[9], r[10], r[11]] })),
    { label: 'Total', variant: 'total', values: [rows[0][16], rows[0][17], rows[0][18], rows[0][19]] },
    { label: 'Grand Total', variant: 'grand', values: [rows[0][20], rows[0][21], rows[0][22], rows[0][23]] },
  ];

  return (
    <Panel>
      <FilterBar label="Current Month" />
      {meomRows.length > 0 && (
        <TableBlock eyebrow="Mid month" title="Subbie breakdown" badge={meomDate} labelHead="Subbie" headers={MONEY_COLS} rows={midData} />
      )}
      <TableBlock eyebrow="End of month" title="Subbie breakdown" badge={eomDate} labelHead="Subbie" headers={MONEY_COLS} rows={eomData} />
    </Panel>
  );
};

/* =========================================================================
 * Higher View panel (dynamic)
 * ===================================================================== */

const HV_COLS = ['Inc GST / Gross', 'Ex GST / Net', 'GST / PAYG', 'Super'];
const ZERO = '$0.00';

const HigherPanel = ({ fyStart, setFyStart, baseFyStart, selected, setSelected, monthFocus, setMonthFocus }) => {
  const now = useMemo(() => new Date(), []);
  const currentMonthNum = now.getMonth() + 1;

  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* Fetch every FY month with all expense blocks; only refetches on FY change. */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchHigherViewFYAll(fyStart);
        if (cancelled) return;
        if (res.status === 'success') setMonths(res.months || []);
        else throw new Error(res.message || 'Failed to load Higher View data');
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load Higher View data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [fyStart]);

  const expenseChips = EXPENSE_CHIPS.filter((c) => c.value !== 'Total');
  const selectedExpenses = selected.filter((v) => v !== 'Total');
  const showBackendTotal = selected.includes('Total');

  /* Which expense chips become rows: none selected -> all of them. */
  const chipsToShow = selectedExpenses.length
    ? expenseChips.filter((c) => selectedExpenses.includes(c.value))
    : expenseChips;

  const displayedMonths = useMemo(() => {
    if (monthFocus == null) return months;
    return months.filter(
      (m) => MONTHS.findIndex((n) => n.toLowerCase() === String(m.month).toLowerCase()) + 1 === monthFocus
    );
  }, [months, monthFocus]);

  /* Build the expense rows for one month from the chips to show. */
  const rowsFor = (m) => {
  const rows = chipsToShow.map((c) => {
    const b = m.blocks ? m.blocks[c.value] : null;
    const values = b ? [b[3], b[4], b[5], b[6]] : [ZERO, ZERO, ZERO, ZERO];
    return { label: c.label, values };
  });

const sums = [0, 0, 0, 0];
  rows.forEach((r) => r.values.forEach((v, i) => { sums[i] += parseCurrency(v); }));
  rows.push({ label: 'Grand Total', variant: 'grand', values: sums.map(fmt) });

  /* Optional: if the Grand Total chip is active, also show the backend's
   * all-expenses total for comparison. */
  if (showBackendTotal && m.blocks && m.blocks.Total) {
    const t = m.blocks.Total;
    rows.push({ label: 'Grand Total (all)', variant: 'grand', values: [t[3], t[4], t[5], t[6]] });
  }
  return rows;
};
  /* Totals sum the shown expense rows, excluding the Grand Total row to avoid
   * double counting (unless Grand Total is the only thing shown). */
  const totals = useMemo(() => {
    const acc = { gross: 0, net: 0, gst: 0, super: 0 };
    displayedMonths.forEach((m) => {
      const rws = rowsFor(m);
      const nonGrand = rws.filter((r) => r.variant !== 'grand');
      const summable = nonGrand.length ? nonGrand : rws;
      summable.forEach((r) => {
        acc.gross += parseCurrency(r.values[0]);
        acc.net += parseCurrency(r.values[1]);
        acc.gst += parseCurrency(r.values[2]);
        acc.super += parseCurrency(r.values[3]);
      });
    });
    return acc;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedMonths, selected]);

  const monthMinus = () => setMonthFocus((m) => stepMonth(m ?? currentMonthNum, -1));
  const monthPlus = () => setMonthFocus((m) => stepMonth(m ?? currentMonthNum, +1));
  const monthCurrent = () => { setFyStart(baseFyStart); setMonthFocus(currentMonthNum); };
  const showAllMonths = () => setMonthFocus(null);
  const yearMinus = () => setFyStart((s) => s - 1);
  const yearPlus = () => setFyStart((s) => s + 1);
  const yearCurrent = () => { setFyStart(baseFyStart); setMonthFocus(null); };

  const toggleChip = (value) => setSelected((s) => (s.includes(value) ? s.filter((v) => v !== value) : [...s, value]));
  const clearChips = () => setSelected([]);

  const focusMonthName = monthFocus ? MONTHS[monthFocus - 1] : null;
  const focusYear = monthFocus ? yearForMonthInFY(monthFocus, fyStart) : null;
  const fyLabel = `FY ${fyStart}\u2013${fyStart + 1}  (July ${fyStart} \u2013 June ${fyStart + 1})`;

  const NavGroup = ({ label, onMinus, onCurrent, onPlus }) => (
    <div style={S.navGroup}>
      <span style={S.navLabel}>{label}</span>
      <button type="button" style={S.navBtn} onClick={onMinus} aria-label={`${label} minus`}>−</button>
      <button type="button" style={S.navCurrent} onClick={onCurrent}>Current</button>
      <button type="button" style={S.navBtn} onClick={onPlus} aria-label={`${label} plus`}>+</button>
    </div>
  );

  return (
    <Panel>
      <FilterBar label="Higher Level View" />

      <div className="fr-filter-chips" style={{ padding: 0, marginBottom: 6 }}>
        {EXPENSE_CHIPS.map((c) => {
          const active = selected.includes(c.value);
          return (
            <button
              key={c.value}
              type="button"
              className={`fr-chip${active ? ' fr-chip-active' : ''}`}
              style={active ? { background: 'rgba(34,211,238,0.15)', color: '#22d3ee', borderColor: 'rgba(34,211,238,0.5)' } : undefined}
              onClick={() => toggleChip(c.value)}
            >
              {c.label}
            </button>
          );
        })}
      </div>
      {selected.length > 0 && (
        <button type="button" onClick={clearChips} style={{ ...S.link, marginBottom: 12 }}>Clear chips</button>
      )}

      <div style={S.nav}>
        <NavGroup label="Month" onMinus={monthMinus} onCurrent={monthCurrent} onPlus={monthPlus} />
        <NavGroup label="Year" onMinus={yearMinus} onCurrent={yearCurrent} onPlus={yearPlus} />
        <div style={S.selection}>
          {fyLabel}
          {monthFocus
            ? <> &middot; Showing <strong>{focusMonthName} {focusYear}</strong> &middot; <button type="button" onClick={showAllMonths} style={S.link}>Show all months</button></>
            : <> &middot; Showing all months</>}
        </div>
      </div>

      <div style={S.totalsWrap}>
        <div style={S.totalPill}><div style={S.totalLabel}>Inc GST / Gross</div><div style={S.totalValue}>{fmt(totals.gross)}</div></div>
        <div style={S.totalPill}><div style={S.totalLabel}>Ex GST / Net</div><div style={S.totalValue}>{fmt(totals.net)}</div></div>
        <div style={S.totalPill}><div style={S.totalLabel}>GST / PAYG</div><div style={S.totalValue}>{fmt(totals.gst)}</div></div>
        <div style={S.totalPill}><div style={S.totalLabel}>Super</div><div style={S.totalValue}>{fmt(totals.super)}</div></div>
      </div>

      {loading ? <Loading text="Loading higher view…" />
        : error ? <div className="ob-info-banner ob-info-banner-warning"><ErrorIcon /> {error}</div>
        : displayedMonths.length === 0 ? <Empty text="No records for this selection." />
        : displayedMonths.map((m) => (
            <TableBlock
              key={`${m.month}-${m.year}`}
              badge={`${m.month} ${m.year}`}
              labelHead="Expense"
              headers={HV_COLS}
              rows={rowsFor(m)}
            />
          ))}
    </Panel>
  );
};

/* =========================================================================
 * Main component
 * ===================================================================== */

const HigherView = () => {
  const [activeTab, setActiveTab] = useState('higher');
  const [staticData, setStaticData] = useState(null);
  const [staticLoading, setStaticLoading] = useState(true);
  const [staticError, setStaticError] = useState(null);
  const [toast, setToast] = useState(null);

  const baseFyStart = useMemo(() => fyStartForDate(new Date()), []);
  const [fyStart, setFyStart] = useState(baseFyStart);
  const [selected, setSelected] = useState([]);       // Higher View expense chips; [] = all
  const [monthFocus, setMonthFocus] = useState(null); // Higher View focused month; null = all

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = filterTemplateStyles;
    document.head.appendChild(link);
    return () => { document.head.removeChild(link); };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetchHigherViewStatic();
        if (res.status === 'success') {
          setStaticData({
            employee: (res.employee && res.employee[0]) || null,
            subbie: (res.subbie && res.subbie[0]) || null,
            current: res.current || [],
          });
        } else {
          throw new Error(res.message || 'Failed to load dashboard data');
        }
      } catch (err) {
        setStaticError(err.message || 'Failed to load dashboard data');
      } finally {
        setStaticLoading(false);
      }
    })();
  }, []);

  const activeIndex = TABS.findIndex((t) => t.key === activeTab);
  const meta = TAB_META[activeTab];

  const renderPanel = () => {
    if (activeTab === 'higher') return <HigherPanel fyStart={fyStart} setFyStart={setFyStart} baseFyStart={baseFyStart} selected={selected} setSelected={setSelected} monthFocus={monthFocus} setMonthFocus={setMonthFocus} />;
    if (staticLoading) return <Panel><Loading text="Loading dashboard…" /></Panel>;
    if (staticError) return <Panel><div className="ob-info-banner ob-info-banner-warning"><ErrorIcon /> {staticError}</div></Panel>;
    if (activeTab === 'employee') return <StaticPanel subject="Employee" filterLabel="Dashboard Employee" rowLabels={['Gross', 'PAYG', 'Net', 'Super']} row={staticData?.employee} />;
    if (activeTab === 'subbie') return <StaticPanel subject="Subbies" filterLabel="Dashboard Subcontractors" rowLabels={['Gross Inc GST', 'GST', 'PO Value', 'Super']} row={staticData?.subbie} />;
    if (activeTab === 'current') return <CurrentPanel rows={staticData?.current} />;
    return null;
  };

  return (
    <div style={{ width: '100%', maxWidth: '100%' }}>
      <Toast toast={toast} />

      {/* header + tabs */}
      <div style={{ ...S.panel, marginBottom: 16 }}>
        <div style={S.panelAccent} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
          <h3 style={S.title}>{meta.title}</h3>
          <HelpButton helpData={[meta.help]} />
          <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
            {meta.downloads.map((d, i) => {
              const fetchFile = d.needsFy
                ? () => downloadHigherviewFY(fyStart, selected, monthFocus)
                : d.fetch;
              const filename = d.needsFy ? `higherview_FY${fyStart}-${fyStart + 1}.xlsx` : d.filename;
              return (
                <DownloadAction
                  key={i}
                  filename={filename}
                  title={d.title}
                  fetchFile={fetchFile}
                  setToast={setToast}
                  icon={<SheetIcon />}
                />
              );
            })}
          </div>
        </div>

        <div className="fr-tab-bar" style={{ background: 'transparent', borderBottom: 'none' }}>
          <div className="fr-tab-list">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                className={`fr-tab-item${activeTab === t.key ? ' fr-tab-active' : ''}`}
                onClick={() => setActiveTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="fr-tab-track">
            <div className="fr-tab-indicator" style={{ left: `${activeIndex * 25}%`, width: '25%' }} />
          </div>
        </div>
      </div>

      {renderPanel()}
    </div>
  );
};

export default HigherView;